/**
 * Scroll Highlight Utility
 *
 * A reusable utility for scrolling to elements and highlighting them with animation.
 * Supports two main use cases:
 * 1. Direct scroll + highlight (clicking a link/button to jump to an element)
 * 2. Tab switch + scroll + highlight (deep linking with URL hash)
 *
 * CSS classes are defined in scss/themes/shared/components/_utilities.scss:
 * - .highlight-flash: Single 2s fade animation (good for table rows, cards)
 * - .blink-highlight: Repeating blink animation (good for form fields, buttons)
 *
 * Usage Examples:
 * ---------------
 *
 * 1. Simple scroll and highlight:
 *    ScrollHighlight.scrollTo('#my-element');
 *
 * 2. With options:
 *    ScrollHighlight.scrollTo('#my-element', {
 *      offset: 100,                    // Scroll offset from top (default: 100)
 *      duration: 400,                  // Scroll animation duration ms (default: 400)
 *      highlightClass: 'blink-highlight', // CSS class for animation (default: 'highlight-flash')
 *      highlightDuration: 3000,        // How long to keep class applied (default: 2000)
 *      onComplete: function($el) {}    // Callback after scroll completes
 *    });
 *
 * 3. Scroll to data attribute selector:
 *    ScrollHighlight.scrollTo('[data-asset-type="backdrop"]');
 *
 * 4. For form fields, highlight parent container:
 *    ScrollHighlight.scrollTo('#my-input', {
 *      highlightParent: '.form-group'  // Selector for parent to highlight instead
 *    });
 *
 * 5. Parse URL hash for deep linking:
 *    // URL: /page#section&highlight=my-element
 *    const parsed = ScrollHighlight.parseHash();
 *    // Returns: { section: 'section', highlight: 'my-element', params: {} }
 *
 * 6. Full deep-link handling with tab switch:
 *    ScrollHighlight.handleDeepLink({
 *      hashPrefix: 'admin-tab',         // The hash to match (e.g., #admin-tab)
 *      onMatch: function(parsed) {      // Called when hash matches
 *        // Switch to correct tab
 *        $('#tabs').foundation('selectTab', '#admin-panel');
 *      },
 *      highlightClass: 'blink-highlight',
 *      highlightDuration: 3000
 *    });
 */

(function() {
  'use strict';

  var ScrollHighlight = {
    // Default options
    defaults: {
      offset: 100,
      duration: 400,
      highlightClass: 'highlight-flash',
      highlightDuration: 2000,
      highlightParent: null,
      onComplete: null
    },

    /**
     * Scroll to an element and highlight it
     * @param {string|jQuery|Element} target - Selector, jQuery object, or DOM element
     * @param {Object} options - Configuration options
     */
    scrollTo: function(target, options) {
      var opts = $.extend({}, this.defaults, options);
      var $target = $(target);

      if (!$target.length) {
        console.warn('ScrollHighlight: Target not found:', target);
        return;
      }

      // Determine what element to highlight
      var $highlightTarget = $target;
      if (opts.highlightParent) {
        var $parent = $target.closest(opts.highlightParent);
        if ($parent.length) {
          $highlightTarget = $parent;
        }
      }

      // For input elements, try to find a reasonable parent
      if ($target.is('input, select, textarea') && !opts.highlightParent) {
        var $formParent = $target.closest('li.s1, li.s2, .form-group, label, .input-group');
        if ($formParent.length) {
          $highlightTarget = $formParent;
        }
      }

      // Calculate scroll position
      var scrollTop = $highlightTarget.offset().top - opts.offset;

      // Perform scroll animation
      $('html, body').animate(
        { scrollTop: scrollTop },
        opts.duration,
        function() {
          // Add highlight class
          $highlightTarget.addClass(opts.highlightClass);

          // Remove highlight class after duration
          setTimeout(function() {
            $highlightTarget.removeClass(opts.highlightClass);
          }, opts.highlightDuration);

          // Call completion callback
          if (typeof opts.onComplete === 'function') {
            opts.onComplete($highlightTarget);
          }
        }
      );
    },

    /**
     * Highlight an element without scrolling
     * @param {string|jQuery|Element} target - Selector, jQuery object, or DOM element
     * @param {Object} options - Configuration options
     */
    highlight: function(target, options) {
      var opts = $.extend({}, this.defaults, options);
      var $target = $(target);

      if (!$target.length) {
        console.warn('ScrollHighlight: Target not found:', target);
        return;
      }

      // Add highlight class
      $target.addClass(opts.highlightClass);

      // Remove highlight class after duration
      setTimeout(function() {
        $target.removeClass(opts.highlightClass);
      }, opts.highlightDuration);
    },

    /**
     * Parse URL hash for deep linking
     * Supports formats like:
     *   #section-name
     *   #section-name&highlight=element-id
     *   #section-name&highlight=element-id&other=value
     *
     * @param {string} hash - Optional hash to parse (defaults to window.location.hash)
     * @returns {Object} Parsed result with section, highlight, and params
     */
    parseHash: function(hash) {
      hash = hash || window.location.hash;

      if (!hash || hash === '#') {
        return { section: null, highlight: null, params: {} };
      }

      // Remove leading #
      hash = hash.substring(1);

      // Split by &
      var parts = hash.split('&');
      var section = parts[0] || null;
      var params = {};
      var highlight = null;

      // Parse parameters
      for (var i = 1; i < parts.length; i++) {
        var pair = parts[i].split('=');
        if (pair.length === 2) {
          var key = decodeURIComponent(pair[0]);
          var value = decodeURIComponent(pair[1]);
          params[key] = value;
          if (key === 'highlight') {
            highlight = value;
          }
        }
      }

      return {
        section: section,
        highlight: highlight,
        params: params
      };
    },

    /**
     * Handle deep linking with optional tab switching
     * Call this on document ready to handle URL hash-based navigation
     *
     * @param {Object} options - Configuration options
     * @param {string} options.hashPrefix - The hash prefix to match (e.g., 'admin-tab')
     * @param {function} options.onMatch - Callback when hash matches (receives parsed hash)
     * @param {number} options.delay - Delay before scrolling (default: 300ms, allows tabs to render)
     * @param {string} options.highlightClass - CSS class for highlighting
     * @param {number} options.highlightDuration - Duration to keep highlight
     */
    handleDeepLink: function(options) {
      var self = this;
      var opts = $.extend({
        hashPrefix: null,
        onMatch: null,
        delay: 300,
        highlightClass: 'blink-highlight',
        highlightDuration: 3000
      }, options);

      var parsed = this.parseHash();

      // Check if we have a matching section
      if (!parsed.section) {
        return false;
      }

      // Check if hashPrefix matches
      if (opts.hashPrefix && parsed.section !== opts.hashPrefix) {
        return false;
      }

      // Call the match callback (e.g., to switch tabs)
      if (typeof opts.onMatch === 'function') {
        opts.onMatch(parsed);
      }

      // If there's a highlight parameter, scroll to and highlight the element
      if (parsed.highlight) {
        setTimeout(function() {
          self.scrollTo('#' + parsed.highlight, {
            highlightClass: opts.highlightClass,
            highlightDuration: opts.highlightDuration
          });
        }, opts.delay);
      }

      return true;
    },

    /**
     * Bind click handlers to elements with data attributes
     * Use this for validation issue links, table of contents, etc.
     *
     * @param {string} selector - Selector for clickable elements
     * @param {Object} options - Configuration options
     * @param {string} options.targetAttr - Data attribute containing target selector (default: 'data-scroll-to')
     * @param {string} options.sectionAttr - Data attribute for section (default: 'data-section')
     * @param {string} options.externalAttr - Data attribute for external URL (default: 'data-external')
     * @param {function} options.onBeforeScroll - Callback before scrolling (can switch tabs, etc.)
     */
    bindClickHandlers: function(selector, options) {
      var self = this;
      var opts = $.extend({
        targetAttr: 'data-scroll-to',
        sectionAttr: 'data-section',
        externalAttr: 'data-external'
      }, options, this.defaults);

      // Unbind first to prevent double-binding
      $(document).off('click.scrollHighlight', selector);

      $(document).on('click.scrollHighlight', selector, function(e) {
        e.preventDefault();
        var $btn = $(this);

        // Check for external URL
        var external = $btn.attr(opts.externalAttr) || $btn.data('external');
        if (external) {
          window.open(external, '_blank');
          return;
        }

        // Get target selector
        var scrollTo = $btn.attr(opts.targetAttr) || $btn.data('scroll-to');
        if (!scrollTo) {
          console.warn('ScrollHighlight: No target specified on element', $btn);
          return;
        }

        // Build target selector
        var targetSelector = scrollTo;

        // If it's a simple value (not a CSS selector), try common patterns
        if (!scrollTo.startsWith('#') && !scrollTo.startsWith('.') && !scrollTo.startsWith('[')) {
          // Try data-asset-type first (for bloodstream preview tables)
          var $dataTarget = $('tr[data-asset-type="' + scrollTo + '"]');
          if ($dataTarget.length) {
            targetSelector = $dataTarget;
          } else {
            // Try as an ID
            targetSelector = '#' + scrollTo;
          }
        }

        // Call onBeforeScroll if provided (for tab switching, etc.)
        if (typeof opts.onBeforeScroll === 'function') {
          opts.onBeforeScroll($btn, scrollTo);
        }

        // Scroll to and highlight
        self.scrollTo(targetSelector, opts);
      });
    }
  };

  // Export to window for global access
  window.StudioDome = window.StudioDome || {};
  window.StudioDome.ScrollHighlight = ScrollHighlight;

  // Also export as standalone for direct access
  window.ScrollHighlight = ScrollHighlight;

})();
